/**
 * @fileoverview Hook UI Layer para CustomerReminderStatusSelectorMC. Maneja estado visual, handlers y opciones de UI.
 * @module useCustomerReminderStatusUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook UI Layer para CustomerReminderStatusSelectorMC.
 * Separa la lógica visual y de handlers de la lógica de negocio.
 *
 * @param {Object} reminder - Objeto reminder actual
 * @returns {Object} Estado y handlers de UI
 */
export default function useCustomerReminderStatusUI(reminder) {
  const { t } = useTranslation('InboxMC');
  const confirmAlertRef = useRef(null);
  const [alertTitle, setAlertTitle] = useState('');

  // Handler para abrir alerta de confirmación
  const handleChangeStatus = useCallback((status) => {
    const text = t('reminderStatusSelector.confirm.canceled');
    setAlertTitle(t('reminderStatusSelector.confirm.title', { action: text }));
    confirmAlertRef.current?.toggleAlert();
  }, [t]);

  // Opciones de estado de recordatorio con clases y handlers
  const reminderStatesOptionsMap = useMemo(() => ([
    {
      value: 'scheduled',
      label: t('reminderStatusSelector.status.scheduled'),
      containerClassName: '!tw-bg-yellow-50 !tw-text-yellow-700',
    },
    {
      value: 'processing',
      label: t('reminderStatusSelector.status.processing'),
      containerClassName: '!tw-bg-yellow-50 !tw-text-yellow-700',
    },
    {
      value: 'sent',
      label: t('reminderStatusSelector.status.sent'),
      containerClassName: '!tw-bg-green-50 !tw-text-green-500',
    },
    {
      value: 'failed',
      label: t('reminderStatusSelector.status.failed'),
      containerClassName: '!tw-bg-red-50 !tw-text-red-700',
    },
    {
      value: 'cancelled',
      label: t('reminderStatusSelector.status.canceled'),
      text: t('reminderStatusSelector.status.cancelled'),
      containerClassName: '!tw-bg-red-50 !tw-text-red-700',
      onClick: () => handleChangeStatus('cancelled'),
    },
  ]), [t, handleChangeStatus]);

  // Opciones según estado actual
  const reminderStatusOptions = useMemo(() => {
    if (reminder.status === 'scheduled') {
      return reminderStatesOptionsMap.filter(option => option.value === 'cancelled');
    }
    return [];
  }, [reminder?.status, reminderStatesOptionsMap]);

  // Label del estado actual
  const reminderStatusLabel = useMemo(() => {
    const found = reminderStatesOptionsMap.find(option => option.value === reminder.status);
    return found?.text || found?.label ||  '';
  }, [reminder?.status, reminderStatesOptionsMap]);

  // Clase de estado actual
  const containerClassNameStatus = useMemo(() => {
    const found = reminderStatesOptionsMap.find(option => option.value === reminder.status);
    return found ? found.containerClassName : '';
  }, [reminder?.status, reminderStatesOptionsMap]);

  // Mensaje de alerta traducido
  const alertMessage = t('reminderStatusSelector.alertMessage');

  // Handler para cancelar
  const handleCancel = useCallback(() => {
    confirmAlertRef.current?.toggleAlert();
  }, []);

  return {
    confirmAlertRef,
    alertTitle,
    setAlertTitle,
    reminderStatusLabel,
    containerClassNameStatus,
    reminderStatusOptions,
    alertMessage,
    handleChangeStatus,
    handleCancel,
  };
}
